import React from 'react';
import Button from '../../ui/Button';
import { Check, Star } from 'lucide-react';

const GhlPricing: React.FC = () => {
  const handleSubscribe = (tier: string) => {
    // Navigate to auth/signup with tier param
    window.location.href = `/auth?plan=${tier}`;
  };

  return (
    <section id="pricing" className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">Simple Pricing for GHL Marketers</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Monthly subscriptions with credits. Use credits to search, export, and verify.
            No long-term contracts. Cancel anytime.
          </p>
        </div>

        {/* SUBSCRIPTION TIERS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto mb-16">

          {/* STARTER */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 flex flex-col">
            <h3 className="text-xl font-bold text-white mb-2">Starter</h3>
            <div className="flex items-baseline mb-4">
              <span className="text-4xl font-bold text-white">$99</span>
              <span className="text-slate-500 ml-1">/mo</span>
            </div>
            <p className="text-sm text-slate-400 mb-8 border-b border-slate-800 pb-6">
              For solo GHL users running 1-2 client accounts.
            </p>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                5,000 Credits / mo
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                CSV Export (GHL format)
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                All Available States
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                Email Support
              </li>
            </ul>
            <Button variant="secondary" className="w-full" onClick={() => handleSubscribe('starter')}>
              Start Free Trial
            </Button>
          </div>

          {/* TEAM */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 flex flex-col">
            <h3 className="text-xl font-bold text-white mb-2">Team</h3>
            <div className="flex items-baseline mb-4">
              <span className="text-4xl font-bold text-white">$199</span>
              <span className="text-slate-500 ml-1">/user/mo</span>
            </div>
            <p className="text-sm text-slate-400 mb-8 border-b border-slate-800 pb-6">
              For small GHL agencies with shared sub-accounts.
            </p>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                Shared Credit Pool
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                Min 3 Seats
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                Admin Dashboard
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                Team API Keys
              </li>
            </ul>
            <Button variant="secondary" className="w-full" onClick={() => handleSubscribe('team')}>
              Create Team
            </Button>
          </div>

          {/* GROWTH - RECOMMENDED */}
          <div className="bg-slate-900 border-2 border-emerald-500 rounded-2xl p-8 flex flex-col relative transform md:scale-105 shadow-2xl shadow-emerald-900/20">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
              <Star className="w-3 h-3" /> BEST FOR GHL
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Growth</h3>
            <div className="flex items-baseline mb-4">
              <span className="text-4xl font-bold text-white">$249</span>
              <span className="text-slate-500 ml-1">/mo</span>
            </div>
            <p className="text-sm text-slate-400 mb-8 border-b border-emerald-500/20 pb-6">
              For GHL agencies scaling to 5-10+ client accounts.
            </p>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start text-sm text-white font-medium">
                <Check className="w-4 h-4 text-emerald-400 mr-2 mt-0.5 shrink-0" />
                15,000 Credits / mo
              </li>
              <li className="flex items-start text-sm text-white font-medium">
                <Check className="w-4 h-4 text-emerald-400 mr-2 mt-0.5 shrink-0" />
                Unlimited Searches
              </li>
              <li className="flex items-start text-sm text-white font-medium">
                <Check className="w-4 h-4 text-emerald-400 mr-2 mt-0.5 shrink-0" />
                API Access (automate GHL import)
              </li>
              <li className="flex items-start text-sm text-white font-medium">
                <Check className="w-4 h-4 text-emerald-400 mr-2 mt-0.5 shrink-0" />
                New License Alerts
              </li>
              <li className="flex items-start text-sm text-white font-medium">
                <Check className="w-4 h-4 text-emerald-400 mr-2 mt-0.5 shrink-0" />
                Priority Support
              </li>
            </ul>
            <Button variant="primary" className="w-full bg-emerald-600 hover:bg-emerald-500 shadow-emerald-900/20" onClick={() => handleSubscribe('growth')}>
              Start Free Trial
            </Button>
          </div>

          {/* AGENCY */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 flex flex-col">
            <h3 className="text-xl font-bold text-white mb-2">Agency</h3>
            <div className="flex items-baseline mb-4">
              <span className="text-4xl font-bold text-white">$499</span>
              <span className="text-slate-500 ml-1">/mo</span>
            </div>
            <p className="text-sm text-slate-400 mb-8 border-b border-slate-800 pb-6">
              For high-volume GHL agencies and SaaS resellers.
            </p>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                30,000 Credits / mo
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                White Label Reports
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                API + Webhooks
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                Dedicated Account Mgr
              </li>
              <li className="flex items-start text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 mr-2 mt-0.5 shrink-0" />
                Bulk Export to R2
              </li>
            </ul>
            <Button variant="secondary" className="w-full" onClick={() => handleSubscribe('agency')}>
              Contact Sales
            </Button>
          </div>

        </div>

        {/* ONE-TIME DATA PACKS CALLOUT */}
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-slate-900 to-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-bold text-white mb-3">Need a One-Time Data Dump?</h3>
          <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
            We also offer one-time state data packs starting at $297. Get the complete licensed professional
            database for any state as a single CSV download. Perfect for a one-off GHL campaign.
          </p>
          <a href="/#pricing" className="text-emerald-400 hover:text-emerald-300 font-bold text-sm transition-colors">
            View Data Packs on Main Site &rarr;
          </a>
        </div>
      </div>
    </section>
  );
};

export default GhlPricing;
